import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGames, getGame, getGameByTitle, getGameMusic,
	getCsrf, getMe, postLogin, postLogout, postRegister, postGameAccess,
	getGameOwnerships, getSubscriptionPlans, getSubscriptions, postCancelSubscription,
	postGameCheckout, postSubscriptionChange, postSubscriptionCheckout, postSubscriptionRenew, getPlayHistory,
	getTournamentHistory, getPocketPlaySurvey, savePocketPlaySurvey, getGameSaves, deleteGameSave,
	getReferralSummary, postReferralCode, postResendVerificationEmail, postVerifyEmail,
	postPhoneSendCode, postPhoneVerify, postPhoneLoginSendCode, postPhoneLogin,
	postPasswordResetSendCode, postPasswordResetConfirm
 } from './api_funcs';

const keys = {
  all: ['games'],
  list: (p) => ['games', 'list', p],
  detail: (id) => ['games', 'detail', id],
  music: (id) => ['games', 'music', id],

  csrf: ['auth', 'csrf'],
  me: ['auth', 'me'],

  gameAccess: ['game', 'access'],
  gameCheckout: ['game', 'checkout'],
  gameOwnerships: ['game', 'ownerships'],
  playHistory: ['game', 'play-history'],
  tournamentHistory: ['game', 'tournament-history'],
  subscriptionPlans: ['subscriptions', 'plans'],
  subscriptions: ['subscriptions', 'subscriptions'],
  survey: ['survey', 'pocketplay'],
  referrals: ['referrals', 'summary'],
};

export function useGames(params) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => getGames(params),
    placeholderData: keepPreviousData, // smooth numbered pagination
  });
}

export function useGame(id) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => getGame(id),
    enabled: !!id,
  });
}

export function useGameByTitle(title) {
  return useQuery({
	queryKey: keys.list({ title }),
	queryFn: () => getGameByTitle(title),
	enabled: !!title,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export const gameSavesKey = (gameId) => ['games', 'saves', gameId];

// Saved matches of the logged-in user for one game (newest first, without their state).
export function useGameSaves(gameId, enabled = true) {
  return useQuery({
    queryKey: gameSavesKey(gameId),
    queryFn: () => getGameSaves(gameId),
    enabled: Boolean(gameId) && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useDeleteGameSave(gameId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (saveId) => deleteGameSave(saveId),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameSavesKey(gameId) }),
  });
}

export function useGameMusic(gameId) {
  return useQuery({
    queryKey: keys.music(gameId),
    queryFn: () => getGameMusic(gameId),
    enabled: !!gameId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSubscriptionPlans(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.subscriptionPlans, queryParams],
	queryFn: () => getSubscriptionPlans(queryParams),
	enabled,
  });
}

export function useSubscriptions(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.subscriptions, queryParams],
	queryFn: () => getSubscriptions(queryParams),
	enabled,
  });
}

export function useGameOwnerships(params = {}) {
  const { enabled = true, ...queryParams } = params;
  return useQuery({
	queryKey: [...keys.gameOwnerships, queryParams],
	queryFn: () => getGameOwnerships(queryParams),
	enabled,
  });
}

export function usePlayHistory({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.playHistory,
    queryFn: getPlayHistory,
    enabled,
    staleTime: 60_000,
  });
}

export function useTournamentHistory({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.tournamentHistory,
    queryFn: getTournamentHistory,
    enabled,
    staleTime: 60_000,
  });
}

export function usePocketPlaySurvey({ enabled = true } = {}) {
  return useQuery({
    queryKey: keys.survey,
    queryFn: getPocketPlaySurvey,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useSavePocketPlaySurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: savePocketPlaySurvey,
    onSuccess: (data) => qc.setQueryData(keys.survey, data),
  });
}

// ---- auth hooks ----
// Most cached data is user-specific (e.g. games' user_has_access), so a login/logout must
// drop it; otherwise a list fetched while signed out stays "fresh" for staleTime and shows
// stale lock icons. Keep the CSRF bootstrap and refetch `me` without blanking it.
const refreshAfterAuthChange = (qc) => {
  qc.resetQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
  return qc.invalidateQueries({ queryKey: keys.me });
};

export const useBootstrapCsrf = () =>
  useQuery({ queryKey: keys.csrf, queryFn: getCsrf, staleTime: Infinity });

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: getMe, staleTime: 5 * 60_000 });

export const useLogin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ identifier, password }) => postLogin({ identifier, password }),
    onSuccess: () => refreshAfterAuthChange(qc),
  });
};

export const useLogout = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postLogout(),
    onSuccess: () => refreshAfterAuthChange(qc),
  });
};

export const usePhoneLoginSendCode = () => useMutation({ mutationFn: postPhoneLoginSendCode });

export const usePhoneLogin = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: postPhoneLogin, onSuccess: () => refreshAfterAuthChange(qc) });
};

export const usePasswordResetSendCode = () => useMutation({ mutationFn: postPasswordResetSendCode });

// A reset also signs the user in.
export const usePasswordResetConfirm = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: postPasswordResetConfirm, onSuccess: () => refreshAfterAuthChange(qc) });
};

export const useRegister = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, email, phoneNumber, password1, password2 }) =>
      postRegister({ username, email, phoneNumber, password1, password2 }),
    onSuccess: () => refreshAfterAuthChange(qc),
  });
};

// ---- Game Hooks ----
export const useGameAccess = () => {
  return useMutation({
	mutationFn: postGameAccess,
  });
};

export const useGameCheckout = () => {
  return useMutation({
	mutationFn: postGameCheckout,
  });
};

export const useSubscriptionCheckout = () => {
  return useMutation({
	mutationFn: postSubscriptionCheckout,
  });
};

export const useSubscriptionRenew = () => {
  return useMutation({
	mutationFn: postSubscriptionRenew,
  });
};

export const useCancelSubscription = () => {
  const qc = useQueryClient();
  return useMutation({
	mutationFn: postCancelSubscription,
	onSuccess: () => {
	  qc.invalidateQueries({ queryKey: keys.subscriptions });
	  qc.invalidateQueries({ queryKey: keys.all });
	},
  });
};

export const useSubscriptionChange = () => {
  const qc = useQueryClient();
  return useMutation({
	mutationFn: postSubscriptionChange,
	onSuccess: () => {
	  qc.invalidateQueries({ queryKey: keys.subscriptions });
	  qc.invalidateQueries({ queryKey: keys.all });
	},
  });
};

// ---- Referral Hooks ----
export function useReferralSummary({ enabled = true } = {}) {
  return useQuery({ queryKey: keys.referrals, queryFn: getReferralSummary, enabled });
}

export const useCreateReferralCode = () => {
  const qc = useQueryClient();
  return useMutation({
	mutationFn: postReferralCode,
	onSuccess: (summary) => qc.setQueryData(keys.referrals, summary),
  });
};

// Verifying an email or phone unlocks the referral code, so both refresh `me` and the summary.
const refreshVerification = (qc) => {
  qc.invalidateQueries({ queryKey: keys.me });
  qc.invalidateQueries({ queryKey: keys.referrals });
};

export const useResendVerificationEmail = () => useMutation({ mutationFn: postResendVerificationEmail });

export const useVerifyEmail = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: postVerifyEmail, onSuccess: () => refreshVerification(qc) });
};

export const usePhoneSendCode = () => {
  const qc = useQueryClient();
  // A newly added number shows up in /auth/me/.
  return useMutation({ mutationFn: postPhoneSendCode, onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }) });
};

export const usePhoneVerify = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: postPhoneVerify, onSuccess: () => refreshVerification(qc) });
};
